import { isBindV3, type BlockNode, type DocumentV3, type PageV3, type TemplateDef } from '@docgen/schema';
import { cloneWithNewIds, newPageId } from './doc-ops';
import type { Selection } from '../editor/types';

/**
 * Pure helpers for the page library in the editor: saying which fields a set
 * fills in from each proposal, where an insert lands, and the document a
 * thumbnail renders. No I/O, so each is tested on its own. (Drafting a name
 * and description is the API's job, so BESTie and the editor share one
 * drafter: `suggestLibraryDetails` in page-library.ts.)
 */

/** "a, b and c" */
export function joinWithAnd(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1] ?? ''}`;
}

/**
 * The data the server fills in from each proposal, and how the summary names
 * it. Mirrors the API's server-owned keys; anything else a page binds to is
 * the author's own data, which travels with the item.
 */
const SERVER_FIELD_LABELS: Readonly<Record<string, string>> = {
  client: 'client',
  contact: 'contact',
  event: 'event',
  owner: 'salesperson',
  intro: 'introduction',
  programs: 'programs',
  pricing: 'pricing',
  toc: 'contents',
};
/** The top-level key of a bind path: `client.name` → `client`. */
function rootKey(path: string): string {
  return path.split(/[.[]/)[0] ?? '';
}

function collectBindRoots(value: unknown, aliases: ReadonlySet<string>, into: Set<string>): void {
  if (isBindV3(value)) {
    const root = rootKey(value.$bind);
    if (root && !aliases.has(root)) into.add(root);
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) collectBindRoots(item, aliases, into);
    return;
  }
  if (value !== null && typeof value === 'object') {
    for (const item of Object.values(value)) collectBindRoots(item, aliases, into);
  }
}

function collectBlockRoots(blocks: readonly BlockNode[], aliases: ReadonlySet<string>, into: Set<string>): void {
  for (const block of blocks) {
    let scope = aliases;
    if (block.repeat) {
      const root = rootKey(block.repeat.over);
      if (root && !aliases.has(root)) into.add(root);
      scope = new Set([...aliases, block.repeat.as]);
    }
    collectBindRoots(block.props, scope, into);
    if (block.children) collectBlockRoots(block.children, scope, into);
  }
}

/**
 * The top-level data keys some pages read: their `$bind` paths and the arrays
 * they repeat over. A repeat's loop variable (`p` in `p.name`) is not a key.
 */
export function boundDataKeys(pages: readonly PageV3[]): readonly string[] {
  const into = new Set<string>();
  for (const page of pages) {
    let aliases: ReadonlySet<string> = new Set();
    if (page.repeat) {
      const root = rootKey(page.repeat.over);
      if (root) into.add(root);
      aliases = new Set([page.repeat.as]);
    }
    collectBlockRoots(page.blocks, aliases, into);
  }
  return [...into];
}

/**
 * The save dialog's summary line: how many pages, and which fields fill in
 * from each proposal they're added to, e.g. "3 pages · fills in client, event
 * and programs from each proposal".
 */
export function librarySummaryLine(pages: readonly PageV3[]): string {
  const count = `${pages.length} ${pages.length === 1 ? 'page' : 'pages'}`;
  const keys = boundDataKeys(pages);
  const filled = Object.keys(SERVER_FIELD_LABELS)
    .filter((key) => keys.includes(key))
    .map((key) => SERVER_FIELD_LABELS[key] ?? key);
  return `${count} · ${filled.length > 0 ? `fills in ${joinWithAnd(filled)} from each proposal` : 'the text stays as saved'}`;
}

/** Where pages added "after the selected page" land: after it, or at the end when nothing is selected. */
export function insertIndexAfterSelection(doc: DocumentV3, selection: Selection): number {
  if (selection === null) return doc.pages.length;
  if (selection.kind === 'page') {
    const index = doc.pages.findIndex((page) => page.id === selection.id);
    return index === -1 ? doc.pages.length : index + 1;
  }
  const holds = (blocks: readonly BlockNode[]): boolean =>
    blocks.some((block) => block.id === selection.id || holds(block.children ?? []));
  const index = doc.pages.findIndex((page) => holds(page.blocks));
  return index === -1 ? doc.pages.length : index + 1;
}

/** The page ids from one page to another, inclusive, in document order (for shift-click). */
export function pageRange(pages: readonly PageV3[], fromId: string, toId: string): readonly string[] {
  const from = pages.findIndex((page) => page.id === fromId);
  const to = pages.findIndex((page) => page.id === toId);
  if (to === -1) return [];
  if (from === -1) return [toId];
  const [start, end] = from <= to ? [from, to] : [to, from];
  return pages.slice(start, end + 1).map((page) => page.id);
}

/** Picked page ids in the order they appear in the document (the order they'll be saved in). */
export function inDocumentOrder(pages: readonly PageV3[], picked: readonly string[]): readonly string[] {
  return pages.filter((page) => picked.includes(page.id)).map((page) => page.id);
}

/**
 * The document a thumbnail or preview renders: the proposal's theme and data,
 * with the item's own author data filling only keys the proposal lacks (as
 * the insert will), and just the item's pages.
 */
export function previewDocument(
  base: DocumentV3,
  pages: readonly PageV3[],
  dataFragment: Readonly<Record<string, unknown>> = {},
): DocumentV3 {
  return { ...base, data: { ...dataFragment, ...base.data }, pages: [...pages] };
}

/** A built-in BCE template as a fresh page with new ids, ready to add to a proposal. */
export function pageFromTemplate(template: TemplateDef): PageV3 {
  return {
    id: newPageId(),
    name: template.name,
    ...(template.background !== undefined ? { background: template.background } : {}),
    ...(template.pad !== undefined ? { pad: template.pad } : {}),
    ...(template.repeat !== undefined ? { repeat: template.repeat } : {}),
    blocks: template.blocks.map(cloneWithNewIds),
  };
}

/** A template as a page for a thumbnail (ids are irrelevant there, so none are minted). */
export function templatePreviewPage(template: TemplateDef): PageV3 {
  return {
    id: template.id,
    name: template.name,
    ...(template.background !== undefined ? { background: template.background } : {}),
    ...(template.pad !== undefined ? { pad: template.pad } : {}),
    ...(template.repeat !== undefined ? { repeat: template.repeat } : {}),
    blocks: template.blocks,
  };
}
